import { useState } from "react";
import { Input } from "antd";
import { maskCep, maskCpfCnpj, maskMoney, maskPhone2 } from "../../../utils/mask";
import maskDate, { getMaxBirthDate } from "../../../utils/maskDate";
import { usePropostaForm } from "../context/PropostaFormContext";
import { useProponenteForm } from "../hooks/useProponenteForm";
import { usePropostaToast } from "../hooks/usePropostaToast";
import { normalizeCpfDigits } from "../utils/proponenteIdentity";
import { estadoCivilOptions } from "../utils/propostaConstants";
import PropostaSectionCard from "../components/PropostaSectionCard";
import PropostaSelect from "../components/PropostaSelect";

const maxBirthDate = getMaxBirthDate();

export default function PropostaProponentes() {
  const {
    proposta,
    updateProponentes,
    isReadOnly,
    editingProponenteIndex,
    setEditingProponenteIndex,
    isDark,
  } = usePropostaForm();
  const toast = usePropostaToast();

  const isEditing =
    editingProponenteIndex !== null && editingProponenteIndex >= 0;
  const currentProponente = isEditing
    ? proposta.proponentes[editingProponenteIndex]
    : undefined;

  const {
    form,
    setField,
    handleCpfCnpjChange,
    handleCpfCnpjBlur,
    handlePhoneChange,
    buildProponenteForSave,
    resetForm,
    lookupStatus,
  } = useProponenteForm(currentProponente, isEditing);

  const [showForm, setShowForm] = useState(true); // aberto por padrão

  const handleSave = () => {
    const proponente = buildProponenteForSave();
    const cpfDigits = normalizeCpfDigits(proponente.cpfCnpj);

    if (cpfDigits) {
      const alreadyExists = (proposta.proponentes || []).some(
        (existing, index) => {
          if (isEditing && index === editingProponenteIndex) return false;
          return normalizeCpfDigits(existing.cpfCnpj) === cpfDigits;
        },
      );

      if (alreadyExists) {
        toast.info(
          "Proponente já adicionado!",
          "Este proponente já existe nesta proposta.",
        );
        return;
      }
    }

    const proponentes = [...(proposta.proponentes || [])];
    if (isEditing) {
      proponentes[editingProponenteIndex!] = proponente;
    } else {
      proponentes.push(proponente);
    }
    updateProponentes(proponentes);
    resetForm();
    setShowForm(false);
    setEditingProponenteIndex(null);
  };

  const handleEdit = (index: number) => {
    setEditingProponenteIndex(index);
    setShowForm(true);
  };

  const handleRemove = (index: number) => {
    const proponentes = [...proposta.proponentes];
    proponentes.splice(index, 1);
    updateProponentes(proponentes);
  };

  const handleAddNew = () => {
    setEditingProponenteIndex(null);
    resetForm();
    setShowForm(true);
  };

  if (showForm && !isReadOnly) {
    return (
      <div className="space-y-4">
        <div className="flex flex-col md:flex-row items-center justify-between gap-2">
          <h3 className="font-semibold text-primary text-base">
            {isEditing ? "Editar Proponente" : "Novo Proponente"}
          </h3>
          {lookupStatus === "loading" && (
            <span className="text-xs text-primary">Carregando dados...</span>
          )}
          {lookupStatus === "found" && (
            <span className="text-xs text-green-600">
              Dados do cliente carregados
            </span>
          )}
        </div>

        <PropostaSectionCard title="Dados Pessoais">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="CPF/CNPJ *">
              <Input
                value={form.cpfCnpj}
                maxLength={18}
                placeholder="CPF ou CNPJ do Proponente"
                onChange={(e) =>
                  handleCpfCnpjChange(maskCpfCnpj(e.target.value))
                }
                onBlur={handleCpfCnpjBlur}
              />
            </Field>
            <Field label="Nome *">
              <Input
                value={form.nome}
                placeholder="Nome / Razão Social"
                onChange={(e) => setField("nome", e.target.value)}
              />
            </Field>
            <Field label="RG *">
              <Input
                value={form.rg}
                onChange={(e) => setField("rg", e.target.value)}
              />
            </Field>
            <Field label="Órgão Emissor">
              <Input
                value={form.emissorRG}
                onChange={(e) => setField("emissorRG", e.target.value)}
              />
            </Field>
            <Field label="Data de Nascimento *">
              <Input
                placeholder="DD/MM/AAAA"
                maxLength={10}
                value={form.dataNascimento}
                onChange={(e) =>
                  setField(
                    "dataNascimento",
                    maskDate(e.target.value, maxBirthDate),
                  )
                }
              />
            </Field>
            <Field label="Estado Civil *">
              <PropostaSelect
                className="w-full"
                placeholder="Selecione"
                value={form.estadoCivil || undefined}
                onChange={(v) => setField("estadoCivil", v)}
                options={estadoCivilOptions}
              />
            </Field>
            <Field label="Naturalidade *">
              <Input
                value={form.naturalidade}
                onChange={(e) => setField("naturalidade", e.target.value)}
              />
            </Field>
            <Field label="Nacionalidade *">
              <Input
                value={form.nacionalidade}
                onChange={(e) => setField("nacionalidade", e.target.value)}
              />
            </Field>
            <Field label="Profissão">
              <Input
                value={form.profissao}
                onChange={(e) => setField("profissao", e.target.value)}
              />
            </Field>
            <Field label="Renda Mensal *">
              <Input
                value={form.rendaMensal}
                onChange={(e) =>
                  setField("rendaMensal", maskMoney(e.target.value))
                }
              />
            </Field>
          </div>
        </PropostaSectionCard>

        <PropostaSectionCard title="Contato">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Telefone *">
              <Input
                value={maskPhone2(form.phoneInput || form.telefone || "")}
                placeholder="(00) 00000-0000"
                onChange={(e) => {
                  const masked = maskPhone2(e.target.value);
                  setField("phoneInput", masked);
                  setField("telefone", masked.replace(/\D/g, ""));
                  setField("cod_pais", "+55");
                }}
              />
            </Field>
            <Field label="E-mail *">
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
              />
            </Field>
          </div>
        </PropostaSectionCard>

        <PropostaSectionCard title="Endereço">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="CEP *">
              <Input
                value={form.cep}
                onChange={(e) => setField("cep", maskCep(e.target.value))}
              />
            </Field>
            <Field label="Número">
              <Input
                value={form.numero}
                onChange={(e) => setField("numero", e.target.value)}
              />
            </Field>
            <Field label="Logradouro *">
              <Input
                value={form.logradouro}
                onChange={(e) => setField("logradouro", e.target.value)}
              />
            </Field>
            <Field label="Bairro *">
              <Input
                value={form.bairro}
                onChange={(e) => setField("bairro", e.target.value)}
              />
            </Field>
            <Field label="Cidade *">
              <Input
                value={form.cidade}
                onChange={(e) => setField("cidade", e.target.value)}
              />
            </Field>
            <Field label="Estado *">
              <Input
                value={form.estado}
                onChange={(e) => setField("estado", e.target.value)}
              />
            </Field>
            <Field label="Complemento">
              <Input
                value={form.complemento}
                onChange={(e) => setField("complemento", e.target.value)}
              />
            </Field>
          </div>
        </PropostaSectionCard>

        <div className="flex gap-3 justify-end pt-2">
          <button
            type="button"
            onClick={() => {
              resetForm();
              setShowForm(false);
              setEditingProponenteIndex(null);
            }}
            className={`px-5 py-2 rounded-full border text-sm font-semibold ${
              isDark
                ? "border-gray-600 text-gray-200"
                : "border-gray-300 text-gray-700"
            }`}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="px-5 py-2 rounded-md bg-primary text-white font-semibold text-sm"
          >
            Salvar Proponente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!isReadOnly && (
        <button
          type="button"
          onClick={handleAddNew}
          className="px-4 py-2 rounded-md bg-primary text-white font-semibold text-sm"
        >
          + Adicionar Proponente
        </button>
      )}
      {proposta.proponentes?.length ? (
        proposta.proponentes.map((p, index) => (
          <div
            key={index}
            className={`p-4 rounded-lg border flex justify-between items-start ${
              isDark
                ? "border-gray-700 bg-[#1d2840]"
                : "border-gray-200 bg-white"
            }`}
          >
            <div>
              <p className="font-semibold text-sm">{p.nome}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {p.cpfCnpj}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {p.email}
              </p>
            </div>
            {!isReadOnly && (
              <div className="flex flex-col md:flex-row gap-4">
                <button
                  type="button"
                  onClick={() => handleEdit(index)}
                  className="h-fit p-0 text-primary text-sm font-medium hover:underline"
                >
                  Editar
                </button>
                <button
                  type="button"
                  onClick={() => handleRemove(index)}
                  className="h-fit p-0 text-red-500 text-sm font-medium hover:underline"
                >
                  Remover
                </button>
              </div>
            )}
          </div>
        ))
      ) : (
        <p className="text-gray-500 text-sm py-4 text-center">
          Nenhum proponente adicionado.
        </p>
      )}
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  const { isDark } = usePropostaForm();

  return (
    <div>
      <label
        className={`block text-xs font-medium mb-1 ${
          isDark ? "text-gray-300" : "text-gray-600"
        }`}
      >
        {label}
      </label>
      {children}
    </div>
  );
}
